import { DispatcherEvent } from '@carvana/merch-native';
import { SendMessageToNative } from '@carvana/merch-native/contracts';
import { useDelayedEffect } from '@carvana/merch-react-hooks';
import { emitter, eventNames, useQualiSurvey } from '@carvana/quali-events';
import { SendStateProps } from '@carvana/quali-events/types';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useState } from 'react';
import BrowserCookies from '~/utilities/BrowserCookies';

const DEFAULT_DELAY_MS = 5000;

export type SurveyConfig = {
  analyticsTitle: string;
  delay: number;
  name: string;
  title: string;
};

type UseQualiModalCoreOptions = {
  activeSurvey: SurveyConfig | undefined;
  beforeShow?: () => Promise<boolean>;
  onOpenStateChange?: (isOpen: boolean) => void;
  sendMessageToNative: SendMessageToNative<DispatcherEvent>;
  trackEvent: (name: string, props: Record<string, unknown>) => void;
};

export default function useQualiModalCore({
  activeSurvey,
  beforeShow,
  onOpenStateChange,
  sendMessageToNative,
  trackEvent
}: UseQualiModalCoreOptions) {
  const { showModal } = useQualiSurvey();
  const [isQualiLoaded, setIsQualiLoaded] = useState(false);

  const onReady = useCallback(() => {
    setIsQualiLoaded(true);
  }, []);

  const onCloseHandler = useCallback(() => {
    sendMessageToNative(DispatcherEvent.LeaveFullscreen, {});
  }, [sendMessageToNative]);

  const onSendState = useCallback(
    (values: SendStateProps) => {
      onOpenStateChange?.(values.isOpen);

      if (!values.isOpen || !activeSurvey) return;

      const title = `${activeSurvey.analyticsTitle} Quali Survey`;
      datadogRum.addAction(`Opened ${title}`);
      trackEvent(title, { action: 'viewed' });
      sendMessageToNative(DispatcherEvent.EnterFullscreen, {});
    },
    [activeSurvey, onOpenStateChange, sendMessageToNative, trackEvent]
  );

  const onSubmitHandler = useCallback(() => {
    if (!activeSurvey) return;

    const title = `${activeSurvey.analyticsTitle} Quali Survey`;
    datadogRum.addAction(`Submitted ${title}`);
    trackEvent(title, { action: 'submitted' });
    sendMessageToNative(DispatcherEvent.LeaveFullscreen, {});
  }, [activeSurvey, sendMessageToNative, trackEvent]);

  useEffect(() => {
    emitter.on(eventNames.ready, onReady);
    emitter.on(eventNames.closeModal, onCloseHandler);
    emitter.on(eventNames.submitSuccess, onSubmitHandler);
    emitter.on(eventNames.sendState, onSendState);

    return () => {
      emitter.off(eventNames.ready, onReady);
      emitter.off(eventNames.closeModal, onCloseHandler);
      emitter.off(eventNames.submitSuccess, onSubmitHandler);
      emitter.off(eventNames.sendState, onSendState);
    };
  }, [onCloseHandler, onReady, onSendState, onSubmitHandler]);

  useDelayedEffect(
    () => {
      if (!activeSurvey || !isQualiLoaded) return;

      const open = () => {
        showModal({
          name: activeSurvey.name,
          title: activeSurvey.title,
          commentBoxTitle: 'Please share how we can improve your experience.',
          identifier: BrowserCookies.get('BrowserCookieId') ?? 'Unknown',
          identifierType: 'BrowserCookieId'
        });
      };

      if (beforeShow) {
        void beforeShow().then(canShow => {
          if (canShow) open();
        });

        return;
      }

      open();
    },
    [activeSurvey, beforeShow, isQualiLoaded, showModal],
    activeSurvey?.delay ?? DEFAULT_DELAY_MS
  );
}
